import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { MessageSquare } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  'القسم ١: مفاهيم وتعريفات القياس',
  'القسم ٢: إجراءات جمع البيانات',
  'القسم ٣: نزاهة البيانات واتفاقية بين المراقبين',
  'القسم ٤: عرض البيانات وتمثيلها بيانيًا',
  'القسم ٥: تحليل البيانات وتفسيرها',
  'القسم ٦: التقييم المتقدم (مستوى تحليل الجودة السلوكي)'
];

const ParticipationDialog = ({ student, onClose, onUpdate }) => {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState(CATEGORIES[0]);
  const [participation, setParticipation] = useState({});
  const [statistics, setStatistics] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (student) {
      fetchParticipation();
    }
  }, [student]);

  const fetchParticipation = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/teacher/participation/${student.email}`);
      
      // Build participation map
      const partMap = {};
      response.data.records.forEach(record => {
        const key = `${record.category}_${record.day_number}`;
        partMap[key] = record.participation_count;
      });
      
      setParticipation(partMap);
      setStatistics(response.data.statistics);
    } catch (error) {
      console.error('Error fetching participation:', error);
    } finally {
      setLoading(false);
    }
  };

  const markParticipation = async (category, day, count) => {
    try {
      await axios.post(`${API}/teacher/participation`, {
        student_email: student.email,
        category,
        day_number: day,
        participation_count: parseInt(count) || 0
      });
      
      // Update local state
      const key = `${category}_${day}`;
      setParticipation({ ...participation, [key]: parseInt(count) || 0 });
      
      // Refresh statistics
      fetchParticipation();
      onUpdate();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const getParticipationCount = (category, day) => {
    const key = `${category}_${day}`;
    return participation[key] || 0;
  };

  return (
    <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{t('participationTracking') || 'Participation Tracking'} - {student?.name}</DialogTitle>
      </DialogHeader>
      
      <div className="space-y-4">
        {/* Category Selector */}
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CATEGORIES.map(cat => (
              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Statistics for selected category */}
        {statistics[selectedCategory] && (
          <Card className="bg-purple-50">
            <CardContent className="pt-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-sm text-gray-600">{t('totalParticipations') || 'Total Participations'}</p>
                  <p className="text-2xl font-bold text-purple-600">{statistics[selectedCategory].total_participations}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t('maxPossible') || 'Max Possible'}</p>
                  <p className="text-2xl font-bold text-blue-600">{statistics[selectedCategory].max_participations}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t('participationRate') || 'Participation %'}</p>
                  <p className="text-2xl font-bold text-green-600">{statistics[selectedCategory].percentage}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Participation Grid (24 days, 10 participations each) */}
        <div className="grid grid-cols-4 gap-3">
          {Array.from({ length: 24 }, (_, i) => i + 1).map(day => {
            const count = getParticipationCount(selectedCategory, day);
            return (
              <Card key={day} className={`p-4 ${
                count >= 8 ? 'bg-green-50 border-green-300' :
                count >= 5 ? 'bg-yellow-50 border-yellow-300' :
                count >= 1 ? 'bg-orange-50 border-orange-300' : 'bg-gray-50'
              }`}>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold">{t('day')} {day}</p>
                    <MessageSquare className="h-4 w-4 text-gray-400" />
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="0"
                      max="10"
                      value={count}
                      onChange={(e) => markParticipation(selectedCategory, day, e.target.value)}
                      className="h-8 text-center"
                    />
                    <span className="text-xs text-gray-500">/10</span>
                  </div>
                  
                  {/* Progress bar */}
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-purple-600 h-2 rounded-full transition-all"
                      style={{ width: `${(count / 10) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </DialogContent>
  );
};

export default ParticipationDialog;
